import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { PRICING } from "@/data/site";

export default function PricingCards({ showHeader = true }) {
  return (
    <section className="dq-container py-20 lg:py-28" data-testid="pricing-section" id="pricing">
      {showHeader && (
        <SectionHeader
          center
          eyebrow="Transparent pricing"
          title="Simple plans that scale"
          highlight="with you"
          subtitle="No hidden fees. Clear scopes. Choose the plan that fits your growth stage."
        />
      )}

      <div className="mt-14 grid gap-6 lg:grid-cols-3 lg:items-start">
        {PRICING.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`relative flex h-full flex-col rounded-[2rem] border p-8 ${
              plan.popular
                ? "border-dq-orange bg-dq-ink text-white dq-shadow-orange lg:-mt-4 lg:pb-12"
                : "border-dq-line bg-white"
            }`}
            data-testid={`pricing-card-${plan.name.toLowerCase()}`}
          >
            {plan.popular && (
              <span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-dq-orange px-4 py-1.5 text-xs font-bold text-white">
                <Star className="h-3.5 w-3.5 fill-current" /> Most Popular
              </span>
            )}
            <h3 className={`font-display text-xl font-bold ${plan.popular ? "text-white" : "text-dq-ink"}`}>{plan.name}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${plan.popular ? "text-white/60" : "text-dq-muted"}`}>{plan.tagline}</p>
            <div className="mt-6 flex items-end gap-1">
              <span className={`font-display text-4xl font-extrabold ${plan.popular ? "text-white" : "text-dq-ink"}`}>{plan.price}</span>
              <span className={`pb-1.5 text-sm ${plan.popular ? "text-white/50" : "text-dq-muted"}`}>{plan.period}</span>
            </div>

            <ul className="mt-8 space-y-3.5">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${plan.popular ? "bg-dq-orange text-white" : "bg-dq-orange/10 text-dq-orange"}`}>
                    <Check className="h-3 w-3" />
                  </span>
                  <span className={plan.popular ? "text-white/80" : "text-dq-ink/80"}>{f}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/contact"
              data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
              className={`mt-8 inline-flex w-full items-center justify-center rounded-full px-6 py-3.5 text-[15px] font-semibold transition-[transform,background-color] duration-300 active:scale-[0.98] ${
                plan.popular ? "bg-dq-orange text-white hover:bg-[#f26200]" : "border border-dq-line bg-white text-dq-ink hover:border-dq-ink"
              }`}
            >
              {plan.cta}
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
